<template>
  <div class="rounded-xl bg-white p-5 shadow-md" dir="rtl">
    <h1 class="mb-2 text-xl font-bold text-purple-700">هزینه ارسال</h1>
    <p class="mb-5 text-xs text-gray-500">سفارش‌های ارسال‌شده و تحویل‌شده بر اساس تاریخ ارسال؛ همه مبالغ به تومان است.</p>
    <form class="mb-5 flex flex-wrap items-end gap-3" @submit.prevent="fetchReport(false)">
      <div><label class="mb-1 block text-xs">از تاریخ</label>
        <date-picker v-model="dateFrom" type="date" format="jYYYY-jMM-jDD" display-format="jYYYY/jMM/jDD" auto-submit /></div>
      <div><label class="mb-1 block text-xs">تا تاریخ</label>
        <date-picker v-model="dateTo" type="date" format="jYYYY-jMM-jDD" display-format="jYYYY/jMM/jDD" auto-submit /></div>
      <button :disabled="loading" class="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white disabled:opacity-50">نمایش گزارش</button>
      <button type="button" :disabled="loading" class="rounded-lg border px-4 py-2 text-sm disabled:opacity-50" @click="fetchReport(true)">اول ماه تا امروز</button>
    </form>
    <p v-if="error" role="alert" class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{{ error }}</p>
    <p v-if="loading" class="py-8 text-center text-gray-500">در حال محاسبه...</p>
    <div v-else-if="report">
      <p class="mb-3 text-xs text-gray-600">بازه گزارش: {{ report.date_from }} تا {{ report.date_to }} — {{ report.order_count }} سفارش</p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div class="rounded-xl bg-blue-50 p-5"><p class="mb-3 text-sm text-blue-700">هزینه ارسال دریافتی از مشتریان</p><b class="text-xl">{{ money(report.collected_total) }} تومان</b></div>
        <div class="rounded-xl bg-amber-50 p-5"><p class="mb-3 text-sm text-amber-700">هزینه واقعی ارسال ثبت‌شده</p><b class="text-xl">{{ money(report.actual_total) }} تومان</b></div>
        <div class="rounded-xl p-5" :class="Number(report.difference) < 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'"><p class="mb-3 text-sm">اختلاف دریافتی و واقعی{{ report.missing_cost_count ? ' (سفارش‌های دارای هزینه واقعی)' : '' }}</p><b class="text-xl" dir="ltr">{{ money(report.difference) }} تومان</b></div>
      </div>
      <div v-if="report.missing_cost_count" class="mt-4 rounded-lg bg-yellow-50 p-4 text-sm leading-7 text-yellow-800">
        {{ report.missing_cost_count }} سفارش هزینه واقعی ثبت‌شده ندارد. اختلاف فقط برای {{ report.known_count }} سفارش دارای هزینه واقعی محاسبه شده است؛ دریافتی این سفارش‌ها {{ money(report.compared_collected_total) }} تومان است.
      </div>
      <p class="mt-4 text-xs text-gray-500">اختلاف = دریافتی از مشتری − هزینه واقعی؛ مقدار منفی یعنی هزینه واقعی بیشتر بوده است.</p>
      <p v-if="!report.order_count" class="mt-5 text-center text-gray-500">در این بازه سفارشی ارسال نشده است.</p>
    </div>
    <section class="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4">
      <h2 class="mb-2 text-base font-bold text-blue-800">تکمیل هزینه ارسال از اکسل‌های قبلی</h2>
      <p class="mb-4 text-xs leading-6 text-blue-700">ستون‌های «بارکد» و «هزینه کل» (ریال) خوانده می‌شوند. تطبیق با کد پیگیری ثبت‌شده انجام می‌شود؛ فقط هزینه واقعی خالی تکمیل می‌شود. این عملیات به بازه تاریخ گزارش محدود نیست.</p>
      <div class="flex flex-wrap items-center gap-3">
        <input type="file" accept=".xls,.xlsx,.html,.htm" :disabled="backfillBusy" @change="changeBackfillFile" class="max-w-full text-sm" />
        <button type="button" :disabled="!backfillFile || backfillBusy" class="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50" @click="previewBackfill">{{ backfillBusy ? 'در حال پردازش...' : 'بررسی فایل' }}</button>
      </div>
      <p v-if="backfillError" role="alert" class="mt-3 text-sm text-red-700">{{ backfillError }}</p>
      <p v-if="backfillResult" role="status" class="mt-3 text-sm font-bold text-green-700">{{ backfillResult }}</p>
      <div v-if="backfillPreview.length" class="mt-4">
        <p class="mb-3 text-sm">قابل تکمیل: {{ backfillReadyCount }} سفارش — جمع قابل ثبت: {{ money(backfillTotal) }} تومان</p>
        <div class="max-h-96 overflow-auto rounded-lg bg-white">
          <table class="min-w-full text-xs">
            <thead class="sticky top-0 bg-gray-100"><tr><th class="p-3">ردیف فایل</th><th class="p-3">بارکد</th><th class="p-3">سفارش</th><th class="p-3">هزینه واقعی (تومان)</th><th class="p-3">نتیجه</th></tr></thead>
            <tbody><tr v-for="(row, index) in backfillPreview" :key="index" class="border-t" :class="row.status === 'matched' || row.status === 'updated' ? 'bg-green-50' : ''"><td class="p-3 text-center">{{ row.row_number }}</td><td class="p-3 text-center" dir="ltr">{{ row.tracking_code }}</td><td class="p-3 text-center">{{ row.order_id || '—' }}</td><td class="p-3 text-center">{{ row.actual_shipping_cost == null ? '—' : money(row.actual_shipping_cost) }}</td><td class="p-3">{{ row.message }}</td></tr></tbody>
          </table>
        </div>
        <button type="button" :disabled="!backfillReadyCount || backfillBusy" class="mt-4 rounded-lg bg-green-600 px-4 py-2 text-sm text-white disabled:opacity-50" @click="confirmBackfill">ثبت هزینه سفارش‌های بدون هزینه واقعی</button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";
export default {
  name: 'ShippingCostReport',
  data() { return { dateFrom: '', dateTo: '', report: null, loading: false, error: '', backfillFile: null, backfillFileId: null, backfillRows: [], backfillPreview: [], backfillBusy: false, backfillError: '', backfillResult: '' }; },
  computed: {
    backfillReadyCount() { return this.backfillPreview.filter(row => row.status === 'matched').length; },
    backfillTotal() { return this.backfillPreview.reduce((sum, row) => sum + (row.status === 'matched' ? Number(row.actual_shipping_cost) : 0), 0); },
  },
  mounted() { this.fetchReport(true); },
  methods: {
    changeBackfillFile(event) {
      this.backfillFile = event.target.files[0] || null;
      this.backfillFileId = null;
      this.backfillRows = [];
      this.backfillPreview = [];
      this.backfillError = '';
      this.backfillResult = '';
    },
    async backfillRequest(path, data) {
      const headers = {};
      this.appendToken(headers);
      const response = await axios.request({ url: this.endpoint(`shop/${path}`), method: 'post', headers, data, timeout: 300000 });
      return response.data;
    },
    backfillErrorMessage(error) {
      const data = error.response && error.response.data;
      return data && (data.error || data.detail) || error.message || 'عملیات انجام نشد.';
    },
    backfillMatrixRows(matrix) {
      if (!matrix || matrix.length < 2) throw new Error('فایل ردیف اطلاعات ندارد.');
      const normalize = value => String(value || '').replace(/[يى]/g, 'ی').replace(/ك/g, 'ک').replace(/[\s\u200c_-]/g, '');
      const headers = matrix[0].map(normalize);
      const codeIndex = headers.indexOf('بارکد');
      const costIndex = headers.indexOf('هزینهکل');
      if (codeIndex < 0 || costIndex < 0) throw new Error('ستون‌های «بارکد» و «هزینه کل» در فایل پیدا نشدند.');
      return matrix.slice(1).map((row, index) => ({ row_number: index + 2,
        tracking_code: String(row[codeIndex] == null ? '' : row[codeIndex]).trim(),
        shipping_cost_rials: String(row[costIndex] == null ? '' : row[costIndex]).trim(),
      })).filter(row => (row.tracking_code || row.shipping_cost_rials) && normalize(row.tracking_code) !== 'جمعکل');
    },
    async previewBackfill() {
      if (!this.backfillFile || this.backfillBusy) return;
      this.backfillBusy = true;
      this.backfillError = '';
      this.backfillResult = '';
      this.backfillPreview = [];
      try {
        if (this.backfillFile.size > 10 * 1024 * 1024) throw new Error('حجم فایل نباید بیشتر از ۱۰ مگابایت باشد.');
        const buffer = await this.backfillFile.arrayBuffer();
        const text = new TextDecoder('utf-8').decode(buffer);
        let matrix;
        if (/^\s*(?:<table|<html|<!doctype)/i.test(text)) {
          const table = new DOMParser().parseFromString(text, 'text/html').querySelector('table');
          if (!table) throw new Error('جدولی در فایل پیدا نشد.');
          matrix = Array.from(table.querySelectorAll('tr')).map(row => Array.from(row.querySelectorAll('th,td')).map(cell => cell.textContent.trim()));
        } else {
          const book = XLSX.read(buffer, { type: 'array', raw: false });
          if (!book.SheetNames.length) throw new Error('فایل هیچ برگه‌ای ندارد.');
          matrix = XLSX.utils.sheet_to_json(book.Sheets[book.SheetNames[0]], { header: 1, defval: '', raw: false });
        }
        this.backfillRows = this.backfillMatrixRows(matrix);
        const data = await this.backfillRequest('previewShippingCostBackfill', { rows: this.backfillRows });
        this.backfillPreview = data.rows;
      } catch (error) { this.backfillError = this.backfillErrorMessage(error); }
      finally { this.backfillBusy = false; }
    },
    async confirmBackfill() {
      if (this.backfillBusy || !this.backfillReadyCount) return;
      this.backfillBusy = true;
      this.backfillError = '';
      this.backfillResult = '';
      try {
        if (!this.backfillFileId) {
          const formData = new FormData();
          formData.append('file', this.backfillFile);
          const file = await this.backfillRequest('uploadTrackingCodeImport', formData);
          if (!file.id) throw new Error('ذخیره فایل انجام نشد.');
          this.backfillFileId = file.id;
        }
        const result = await this.backfillRequest('confirmShippingCostBackfill', { rows: this.backfillRows, file_id: this.backfillFileId });
        this.backfillPreview = result.rows;
        this.backfillResult = `هزینه واقعی ${result.updated_count} سفارش تکمیل شد.`;
        this.fetchReport(false);
      } catch (error) { this.backfillError = this.backfillErrorMessage(error); }
      finally { this.backfillBusy = false; }
    },
    money(value) { return Number(value || 0).toLocaleString('fa-IR', { maximumFractionDigits: 2 }); },
    fetchReport(reset = false) {
      if (this.loading) return;
      if (!reset && (!this.dateFrom || !this.dateTo || this.dateFrom > this.dateTo)) {
        this.error = 'بازه تاریخ را کامل و به ترتیب شروع تا پایان انتخاب کنید.';
        return;
      }
      this.loading = true;
      this.error = '';
      this.report = null;
      this.request({
        url: this.endpoint('shop/shippingCostReport'), method: 'get',
        params: reset ? {} : { date_from: this.dateFrom, date_to: this.dateTo },
        success: data => {
          this.report = data;
          this.dateFrom = data.date_from;
          this.dateTo = data.date_to;
          this.loading = false;
        },
        error: error => {
          this.error = error && (error.error || error.detail) || 'دریافت گزارش انجام نشد.';
          this.loading = false;
        },
      });
    },
  },
};
</script>
